/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { setTenant, setPartner } from "@redux/slices/tenantSlice";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { getPartners, getCustomers } from "../request/request";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
interface Partner {
  id: number;
  nameDisplay: string;
  Domain: string;
  chargebeeId: string;
}

interface Customer {
  id: number;
  nameDisplay: string;
  Domain: string;
  customerId?: string | null;
}

interface PartnerCustomerDropdownProps {
  tenantType: string; // "CUSTOMER" | "PARTNER" | "GLOBAL_ADMIN" | ...
  isLoading: boolean;
  isAuthenticated: boolean;
  getAccessTokenSilently: () => Promise<string>;
}

export default function PartnerCustomerDropdown({
  tenantType,
  isLoading,
  isAuthenticated,
  getAccessTokenSilently,
}: PartnerCustomerDropdownProps) {
  if (tenantType === "CUSTOMER") {
    return null;
  }
  const dispatch = useAppDispatch();
  const { partnerId, partnerName, tenantId, tenantName, partnerDomain,tenantDomain } =
    useAppSelector((s) => s.selection);

  const [isFetching, setIsFetching]         = useState(false);
  const [partnersData, setPartnersData]     = useState<Partner[]>([]);
  const [partnerOptions, setPartnerOptions] = useState<string[]>([]);
  const [customersData, setCustomersData]   = useState<Customer[]>([]);
  const [customerOptions, setCustomerOptions] = useState<string[]>([]);
  const overlaySpinner = useProcessingOverlay(isFetching);

  const userProfile = useAppSelector(s => s.userProfile);
  // ─── a single source of truth for the "parent" option ───

  const selection = useAppSelector(s => s.selection);
useEffect(() => {
  // console.log("🛎️ selection slice:", selection);
}, [selection]);

useEffect(() => {
  if (!tenantId || tenantId === partnerId) return; // Don't reset if it's partner view
  if (customersData.length > 0 && !tenantDomain) {
    const cust = customersData.find(c => c.id === tenantId);
    if (cust) {
      dispatch(setTenant({
        id: cust.id,
        name: cust.nameDisplay,
        domain: cust.Domain,
      }));
    }
  }
}, [tenantId, partnerId, customersData, tenantDomain, dispatch]);



  //
    useEffect(() => {
      if (tenantType === "PARTNER" && partnerId === 0) {
        // “Tell Redux who I am before loading customers”
        dispatch(setPartner({
          id:   userProfile.tenantId,
          name: userProfile.Tenant.nameDisplay,
          domain: userProfile.Tenant.Domain,
        }));
      }
    }, [
      tenantType,
      partnerId,
      userProfile.tenantId,
      userProfile.Tenant.nameDisplay,
      dispatch,
    ]);


    //On mount or when tenantType/auth changes, load partners/customers
    useEffect(() => {
      if (isLoading || !isAuthenticated || tenantType === "CUSTOMER") return;
  
      if (tenantType === "GLOBAL_ADMIN") {
        //For GLOBAL_ADMIN users  => fetchPartners
        fetchPartners();
      } else {
        //For PARTNER users  => fetchPartners
        loadCustomers(partnerId);
      }
    }, [tenantType, isLoading, isAuthenticated, partnerId]);

  // --------------------------------------------------
  // 2) Funciones: fetchPartners y loadCustomers
  // --------------------------------------------------
  const fetchPartners = async () => {
    setIsFetching(true);
    try {
      const token = await getAccessTokenSilently();
      const resp = await getPartners(token);
      const raw = resp.data.data || [];
      setPartnersData(raw);
      setPartnerOptions(raw.map((p: any) => p.nameDisplay));
      // decide which partner to pick:
      const persisted = partnerId !== 0 && raw.find((p: any) => p.id === partnerId);
      if (persisted) {
        //skip dispatching setPartner so we don't reset tenantName
        await loadCustomers(partnerId);
      } else {
        // no persisted partner → fall back to first in the list
        const pick = raw[0];
        dispatch(setPartner({ id: pick.id, name: pick.nameDisplay, domain: pick.Domain }));
        await loadCustomers(pick.id);
      }
    } catch (err) {
      console.error("Error fetching partners", err);
    } finally {
      setIsFetching(false);
    }
  };


  // ────────────────────────────────────────────────────────────
  // 2) Fetch customers for a given partnerId
  // ────────────────────────────────────────────────────────────
  const loadCustomers = async (pid: number) => {
    setIsFetching(true);
    try {
      const token = await getAccessTokenSilently();
      // const resp = await getCustomers(token, tenantId);
      const resp = await getCustomers(token, pid);
      const raw = resp.data.data || [];
      setCustomersData(raw);
      // find the display name of the parent account
      const stableParentLabel = `${partnerName} (Partner Account)`;

      // const stableParentLabel = `${userProfile.Tenant.nameDisplay} (Partner Account)`;
      // build options (All tenants + each nameDisplay)
      const opts = [stableParentLabel, ...raw.map((c: any) => c.nameDisplay)];
      setCustomerOptions(opts);

     // ─────────────── DEFAULT TO FIRST CUSTOMER ───────────────
    // If nobody’s picked yet (tenantId is 0 or falsy), pick the very first raw[0]
    if (!tenantId && raw.length > 0) {
      const firstCust = raw[0];
      dispatch(setTenant({
        id:   firstCust.id,
        name: firstCust.nameDisplay,
        domain: firstCust.Domain,
        customerId: firstCust.customerId ?? null,
      }));
      //Set a default—skip the “persisted” logic below
      return;
    }
    // ───────────────────────────────────────────────────────────


      //If we have a persisted tenantId for the partner, restore it:
        if (tenantId && tenantId !== pid) {
          // tenantId belongs to a *different* partner—ignore it
          return;
        }
      const persistedCust = raw.find((c: any) => c.id === tenantId);
      if (persistedCust) {
        // Only persist real customer picks
        dispatch(setTenant({
          id:   persistedCust.id,
          name: persistedCust.nameDisplay,
          domain: persistedCust.Domain,
          customerId: persistedCust.customerId ?? null,
        }));
      }
  } catch (err) {
    console.error("Error fetching customers", err);
  } finally {
    setIsFetching(false);
  }
  };


  // --------------------------------------------------
  // 4) Handlers de selección
  // --------------------------------------------------
    //User selects a partner from the dropdown
    const handleSelectPartner = async (nameDisplay: string) => {
      const found = partnersData.find((p) => p.nameDisplay === nameDisplay);
      if (!found) return;
      dispatch(setPartner({ id: found.id, name: found.nameDisplay, domain: found.Domain}));
      // loading customers for the new partner will reset tenant in Redux
      await loadCustomers(found.id);
    };
  
    // 5) User selects a customer
    const handleSelectCustomer = (label: string) => {
      const stableParentLabel = `${partnerName} (Partner Account)`;
      if (label === stableParentLabel) {
        dispatch(setTenant({
          id: partnerId,         // Partner’s ID
          name: partnerName,
          domain: partnerDomain,
          customerId: null,
        }));
        return; // Prevent further logic
      }
        const found = customersData.find(c => c.nameDisplay === label);
  if (found) {
    dispatch(setTenant({
      id: found.id,
      name: found.nameDisplay,
      domain: found.Domain,
      customerId: found.customerId ?? null,
    }));
  }
}
      // }
      //   if (label === stableParentLabel) {
      //     dispatch(setTenant({
      //       id: partnerId,
      //       name: partnerName,
      //       domain: partnerDomain
      //     }));
    //   } else {
    //     const found = customersData.find(c => c.nameDisplay === label);
    //     if (found) {
    //       dispatch(setTenant({ id: found.id, name: found.nameDisplay, domain: found.Domain}));
    //     }
    //   }
    // };

    // For display: show the decorated parent label if tenantName === partnerName
      const stableParentLabel = `${partnerName} (Partner Account)`;
      const customerDisplay =
      tenantName === partnerName ? stableParentLabel : tenantName;

  // --------------------------------------------------
  // 5) Render condicional
  // --------------------------------------------------


  useEffect(() => {
    if (tenantId && customersData.length > 0 && !tenantDomain) {
      const cust = customersData.find(c => c.id === tenantId);
      if (cust) {
        dispatch(setTenant({
          id:     cust.id,
          name:   cust.nameDisplay,
          domain: cust.Domain,
          customerId: cust.customerId ?? null,
        }));
      }
    }
  }, [tenantId, customersData, tenantDomain, dispatch]);


  return (
    <>
      {/* GLOBAL_ADMIN / PARTNER => partner + possible customer */}
      {tenantType === "GLOBAL_ADMIN" && (
        <div className="flex flex-col">
          <GenericDropdown
            items={partnerOptions}
            value={partnerName}
            onSelect={handleSelectPartner}
            minWidthClass="min-w-[200px]"
            maxWidthClass="max-w-[300px]"
          />
        </div>
      )}

      {(tenantType === "GLOBAL_ADMIN" || tenantType === "PARTNER") && (
        <div className="flex flex-col">
          <GenericDropdown
            items={customerOptions}
            // value={tenantName}
            value={customerDisplay}
            onSelect={handleSelectCustomer}
            minWidthClass="min-w-[200px]"
            maxWidthClass="max-w-[300px]"
          />
        </div>
      )}

      {overlaySpinner}
    </>
  );
}